import { isBicentenary, isZsl } from '@/lib/utils';
import {colors, container, eyebrow, grid, GridContainer, h1, mediaQueries} from '@/theme/lib';
import styled, { css, DefaultTheme } from 'styled-components';
import { getHeroBackgroundPatternProperties } from '@components/BicentenaryPatterns/util';
import { heroBackgroundPattern } from '@components/BicentenaryPatterns/BicentenaryPatterns.styles'
import { HubHeroBackground } from '@/__generated__/graphql';

export interface StyledHeroWrapperProps {
  $backgroundPattern?: HubHeroBackground;
}

const blackCircleGradient = css`
  position: relative;
  z-index: 1;
  &::before {
    content: '';
    display: ${(props) => (props.theme.key === 'zsl' ? 'block' : 'none')};
    background: radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
    width: 150%;
    height: ${(props) => (props.theme.key === 'zsl' ? '150%' : '140%')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
    ${mediaQueries.lg} {
      background: radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%);
      height: ${(props) => (props.theme.key === 'zsl' ? '200%' : '140%')};
    }
  }
`;

export const StyledHero = styled.div`
  margin-bottom: 2rem;
  overflow: hidden;
  position: relative;
  .hero {
    &__wrapper {
      ${container}
      ${grid}
      position: relative;
      grid-template-areas: 'box box box box box';
      ${mediaQueries.lg} {
        grid-template-areas: 'box box box box box . . . . .';
      }
      svg {
        display: none;
      }
    }
  }
  .hero__image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    mask-image: ${(props) =>
    !isZsl(props.theme.key) ? 'url(/Hero/bgMask-mobile.svg)' : undefined};
    mask-repeat: ${(props) => (!isZsl(props.theme.key) ? 'no-repeat' : undefined)};
    mask-position: ${(props) => (!isZsl(props.theme.key) ? 'bottom' : undefined)};
    ${mediaQueries.smHero} {
      mask-image: ${(props) =>
    !isZsl(props.theme.key) ? 'url(/Hero/bgMask-desktop.svg)' : undefined};
    }

    &:before {
      content: '';
      z-index: 1;
      position: absolute;
      display: block;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      background-color: ${colors.black};
      opacity: ${(props) => (props.theme.key === 'zsl' ? 0 : 0.4)};
    }
  }
`;

export const StyledCircleImage = styled.div`
  display: none;
  ${mediaQueries.lg} {
    grid-area: circle;
    position: relative;
    display: block;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    height: 653.5px;
    width: 653.5px;
    margin-top: 23px;
    border-radius: 50%;
    z-index: 9;
    &:after {
      content: '';
      position: absolute;
      top: -20px;
      left: -20px;
      height: 100%;
      width: 100%;
      border: 5px solid ${colors.tiger};
      border-radius: 50%;
      height: 683.5px;
      width: 683.5px;
    }
    img {
      border-radius: 50%;
    }
  }
`;

export const StyledHeroBox = styled.div`
  word-break: normal;
  overflow-wrap: break-word;
  grid-area: box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 0px 0px ${(props) => (props.theme.key === 'zsl' ? '12px' : '32px')};
  background: transparent;
  z-index: 99;

  ${mediaQueries.mdLogo} {
    padding-top: 5rem;
    padding-left: 1rem;
  }
  ${mediaQueries.lg} {
    max-width: 824px;
    gap: 16px;
    padding: 0px 0px 16px;
    margin-top: 6.5rem;
  }
  span {
    ${eyebrow}
    text-transform: initial;
    display: block;
    flex: none;
    order: 1;
    flex-grow: 0;
    color: ${(props) => (props.theme.key === 'zsl' ? colors.parakeet : props.theme.colors.eyebrow)};
    font-size: 16px;
    font-family: ${(props) => props.theme.fonts.body};
    font-weight: 600;
    line-height: 19px;
    margin-bottom: 0.5rem;
    ${mediaQueries.lg} {
      font-size: 32px;
      line-height: 42px;
      margin-bottom: 1rem;
    }
  }
  h1 {
    ${h1}
    flex: none;
    order: 1;
    flex-grow: 0;
    color: ${(props) => (props.theme.key === 'zsl' ? colors.white : colors.zooFont)};
    font-size: 40px;
    line-height: 42px;
    text-transform: ${(props) => (props.theme.key === 'zsl' ? 'uppercase' : undefined)};
    font-weight: ${(props) => (props.theme.key === 'zsl' ? '600' : '800')};
    ${mediaQueries.lg} {
      font-weight: ${(props) => (props.theme.key === 'zsl' ? undefined : '600')};
      font-size: 80px;
      line-height: 84px;
    }
  }
  .hero__cta-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex: none;
    order: 2;
    flex-grow: 0;
    margin-top: 0.5rem;
    ${mediaQueries.lg} {
      gap: 16px;
      margin-top: 1rem;
    }
  }
`;

export const StyledTextOnlyHero = styled(StyledHero)`
  position: relative;
  overflow: hidden;

  ${(props) =>
    isBicentenary(props.theme.key) && css`
     background-color: ${colors.whiteLight};
    `
  }

  .hero {
    &__wrapper {
      position: static;
      &:after {
        display: ${(props) => (isZsl(props.theme.key) ? 'block' : 'none')};
        position: absolute;
        content: '';
        // Todo: get asset from proper place/handle differently
        background-image: ${(props) => (isBicentenary(props.theme.key) ? `url(/Hero/ellipse-200-v2.svg)` : 'url(/Hero/ellipse.svg)')};
        background-size: cover;
        background-repeat: no-repeat;

        background-position: bottom right;
        bottom: 0px;
        height: 100%;
        width: 100%;
        left: 0px;
        ${mediaQueries.mdLogo} {
          top: -5%;
        }
        ${mediaQueries.lg} {
          top: 0;
          bottom: 0px;
          background-position: 50% 100%;
          height: 100%;
          width: 100%;
          left: -15%;
        }
        ${mediaQueries.xl} {
          left: -5%;
        }
      }
      svg {
        display: none;
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0px;
        top: 0px;
        ${mediaQueries.lg} {
          height: 100%;
          width: 50%;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
    &__box {
      height: fit-content;
      align-self: center;
      margin-bottom: ${(props) => isBicentenary(props.theme.key) ? '20px' : '-0.75rem'};
      padding: 9.063rem 0 0 0;
      ${mediaQueries.mdLogo} {
        padding: 11.625rem 0 0;
        margin-bottom: 0;
      }
      ${mediaQueries.lg} {
        padding-bottom: 1rem;
      }

      ${(props) =>
        isBicentenary(props.theme.key) &&
        css`
          .hero-primary,
          .hero-secondary {
            border-color: ${colors.black};
          }
          .hero-primary {
            background-color: ${colors.white};
            color: ${colors.seagrass};
          }
  `}
    }
    &__inner-wrapper {
      background-color: ${(props) => isBicentenary(props.theme.key) ? colors.whiteLight : props.theme.colors.background};
      padding: 0.75rem;
      margin-left: -0.75rem;
      border-radius: 5%;
      max-width: 100%;

      span, h1 {
        ${(props) =>
          isBicentenary(props.theme.key) &&
            css`
            color: ${colors.black};
        `}
      }


      ${mediaQueries.lg} {
        padding: 1rem 1rem 2rem 1rem;
        margin-left: -1rem;
        max-width: none;
      }
    }
  }
`;

export const StyledShortHero = styled(StyledHero)`
  position: relative;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  .hero {
    &__wrapper {
      height: 420px;
      ${mediaQueries.lg} {
        height: 729px;
      }
    }
    &__box {
      align-self: end;
      margin-top: 8.5rem;
      margin-bottom: 2rem;
      ${mediaQueries.lg} {
        align-self: center;
      }
    }
    &__inner-wrapper {
      ${blackCircleGradient}
      max-width: 100%;
      ${mediaQueries.lg} {
        max-width: none;
      }
      span,
      h1 {
        text-shadow: ${(props) =>
    props.theme.key === 'zsl' ? '0px 0px 10px rgba(0, 0, 0, 1)' : undefined};
      }
      h1 {
        color: ${colors.white};
      }
      span {
        color: ${(props) => (props.theme.key !== 'zsl' ? colors.honeyBee : undefined)};
      }
    }
  }
`;

export const StyledCircleHero = styled(StyledHero)`
  ${mediaQueries.lg} {
    background-image: none;
  }
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  .hero {
    &__image {
      ${mediaQueries.lg} {
        display: none;
      }
    }
    &__wrapper {
      height: 420px;
      grid-template-areas: 'box box box box box';
      h1,
      span {
        color: ${(props) => (props.theme.key === 'zsl' ? undefined : colors.white)};
        ${mediaQueries.lg} {
          color: ${(props) => (props.theme.key === 'zsl' ? undefined : colors.zooFont)};
        }

        text-shadow: ${(props) =>
    props.theme.key === 'zsl' ? '0px 0px 10px rgba(0, 0, 0, 1)' : undefined};
      }
      ${mediaQueries.lg} {
        height: 729px;
        grid-template-areas: 'box box box box box circle circle circle circle circle';
        h1,
        span {
          text-shadow: none;
        }
      }
    }
    &__box {
      align-self: end;
      margin-top: 8.5rem;
      margin-bottom: 2rem;
      ${mediaQueries.lg} {
        align-self: center;
      }
    }
    &__inner-wrapper {
      background: radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 100%);
      ${mediaQueries.lg} {
        background: none;
      }
    }
  }
`;

export const StyledVideoHero = styled(StyledHero)`
  position: relative;
  overflow: hidden;
  margin-bottom: ${(props) => props.theme.key === 'zsl' ? '1rem' : '0'};
  mask-image: ${(props) =>
    props.theme.key !== 'zsl' ? 'url(/Hero/bgMask-mobile.svg)' : undefined};
  mask-repeat: ${(props) => (props.theme.key !== 'zsl' ? 'no-repeat' : undefined)};
  mask-position: ${(props) => (props.theme.key !== 'zsl' ? 'bottom' : undefined)};
  ${mediaQueries.smHero} {
    mask-image: ${(props) =>
    props.theme.key !== 'zsl' ? 'url(/Hero/bgMask-desktop.svg)' : undefined};
  }

  ${mediaQueries.md} {
    margin-bottom: 2rem;
  }

  /* max-height: 580px; */
  ${mediaQueries.lg} {
    max-height: 1029px;
  }

  .video-wrapper {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
    height: 0;

    margin-top: 84px;
    ${mediaQueries.md} {
      margin-top: 0;
    }

    &:before {
      content: '';
      z-index: 1;
      position: absolute;
      display: block;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      background-color: ${colors.black};
      opacity: ${(props) => (props.theme.key === 'zsl' ? 0 : 0.3)};
    }
    &--black + .hero__wrapper {
      .hero-primary {
        background: ${colors.white};
        border-color: ${colors.black};
        color: ${colors.black};
        &:focus,
        &:hover {
          background-color: ${colors.white60};
          color: ${colors.black60};
        }
      }
      .hero-secondary {
        background: ${colors.black};
        border-color: ${colors.white};
        color: ${colors.white};
        &:focus,
        &:hover {
          background-color: ${colors.black60};
          color: ${colors.white60};
        }
      }
    }
  }
  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .hero {
    &__wrapper {
      margin-top: 1rem;

      ${mediaQueries.md} {
        margin-top: 0;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;

        h1,
        span {
          text-shadow: ${(props) =>
    props.theme.key === 'zsl' ? '0px 0px 10px rgba(0, 0, 0, 1)' : undefined};
        }
        h1 {
          color: ${colors.white};
        }
      }
      span {
        color: ${(props) => (props.theme.key === 'london' ? colors.seagrass : undefined)};
        color: ${(props) => (props.theme.key === 'whipsnade' ? colors.redPanda : undefined)};
        ${mediaQueries.md} {
          color: ${(props) => (props.theme.key !== 'zsl' ? colors.honeyBee : undefined)};
        }
      }
    }
    &__box {
      align-self: end;
      margin-bottom: 0;
      padding: 0 0 20px;
      ${mediaQueries.md} {
        margin-bottom: 2rem;
      }
      ${mediaQueries.lg} {
        align-self: center;
        margin-top: ${(props) => (props.theme.key === 'zsl' ? '8rem' : '3rem')};
      }
    }
    &__inner-wrapper {
      max-width: 100%;
      ${mediaQueries.md} {
        ${blackCircleGradient}
      }
      ${mediaQueries.lg} {
        max-width: none;
      }
    }
  }
`;

export const StyledTallHero = styled(StyledHero)`
  .hero {
    &__wrapper {
      height: 580px;
      ${mediaQueries.lg} {
        height: 1029px;
        max-height: 90vh;
        min-height: 50rem;
        max-width: ${(props) => (isBicentenary(props.theme.key) ? 'none' : undefined )};
      }
    }
    &__box {
      align-self: end;
      margin-bottom: 4rem;
      ${mediaQueries.lg} {
        align-self: center;
        margin-top: ${(props) => (props.theme.key === 'zsl' ? '8rem' : '3rem')};
        margin-bottom: 0;
      }
    }
    &__inner-wrapper {
      ${blackCircleGradient}
      max-width: 100%;
      ${mediaQueries.lg} {
        max-width: none;
      }
      span,
      h1 {
        text-shadow: ${(props) =>
    props.theme.key === 'zsl' ? '0px 0px 10px rgba(0, 0, 0, 1)' : undefined};
      }
      h1 {
        color: ${colors.white};
      }
      span {
        color: ${(props) => (props.theme.key !== 'zsl' ? colors.honeyBee : undefined)};
      }
    }
  }

  position: relative;
  .swiper {
    width: 100%;
    height: 580px;
    ${mediaQueries.lg} {
      height: 1029px;
      max-height: 90vh;
      min-height: 50rem;
    }
  }
  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
    > * {
      user-select: none;
      touch-action: manipulation;

      ${mediaQueries.md} {
        user-select: auto;
        touch-action: auto;
      }
    }
  }

  .swiper-slide img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Slide controlleer */
  .swiper-pagination {
    display: none;
  }
`;

export const GridContainerTallHero = styled(GridContainer) `
  ${container};
  ${grid};
  width: 100%;
  grid-column: 1 / -1;
  padding: 0!important;
`;

export const StyledHeroWrapper = styled(StyledHero)<StyledHeroWrapperProps>`
  background-image: none;
  background-repeat: none;
  background-size: none;
`;
const getBicentenaryHeroStyling = (
  props: { theme: DefaultTheme } & StyledHeroWrapperProps
) => {
  const patternProperties = getHeroBackgroundPatternProperties(props.$backgroundPattern);

  const style = css`
    background-color: ${patternProperties.backgroundColor};
    ${patternProperties.backgroundImage ? heroBackgroundPattern(patternProperties.backgroundImage, patternProperties.backgroundCurve) : ''}

    .hero__box {
      grid-column: 1 / -1;
      margin-bottom: 1rem;
    }

    .hero-primary {
      color: ${patternProperties.button.primary.text};
      background-color: ${patternProperties.button.primary.bg};
      border-color: ${patternProperties.button.primary.border};
      transition: all 200ms linear;
      &:hover {
        background-color: ${patternProperties.button.primary.hoverBackground};
        border-color: ${patternProperties.button.primary.hoverBorder};
        color: ${patternProperties.button.primary.hoverText};
      }
      &:focus {
        text-decoration: none;
        border-color: ${patternProperties.button.primary.focusBorder};
      }
    }

    .hero-secondary {
      color: ${patternProperties.button.secondary?.text};
      background-color: ${patternProperties.button.secondary?.bg};
      border-color: ${patternProperties.button.secondary?.border};
      transition: all 200ms linear;
      &:hover {
        background-color: ${patternProperties.button.secondary?.hoverBackground};
        border-color: ${patternProperties.button.secondary?.hoverBorder};
        color: ${patternProperties.button.secondary?.hoverText};
      }
      &:focus {
        text-decoration: none;
        border-color: ${patternProperties.button.secondary?.focusBorder};
      }
    }

    .slide-controller {
      bottom: 1rem;
      left: 0;
      &__wrapper {
        max-width: 100%;
      }
    }
  `;

  return style;
};

export const StyledHero200Wrapper = styled(StyledHero)<StyledHeroWrapperProps>`
  margin-bottom: 0;
  ${(props) => getBicentenaryHeroStyling(props)}
`;

export const StyledShortHero200 = styled(StyledHero200Wrapper)<StyledHeroWrapperProps>`
  display: flex;
  min-height: 534px;
  max-height: 729px;
  ${mediaQueries.lg} {
    height: 729px;
  }
  flex-direction: column;
  justify-content: center;
  padding: 60px 0 0 0;
  ${mediaQueries.md} {
    padding: 0;
  }
  position: relative;

  svg {
    display: none;
  }

  .hero {
    &__wrapper {
      width: 100%;
      background: none;
      padding: 1rem;
      flex-shrink: 0;
      margin: 0;
      ${mediaQueries.md} {
        padding: 2rem;
      }
      ${mediaQueries.lg} {
        margin: 0 auto;
      }
    }

    &__image {
      position: relative;
      width: 100%;
      order: -1;
      ${mediaQueries.smHero200} {
        width: 60%;
        margin: 0 auto;
        align-content: flex-end;
      }
      ${mediaQueries.md} {
        order: 1;
        align-content: flex-end;
      }
      ${mediaQueries.tab} {
        position: absolute;
        z-index: 2;
        right: 0;
        left: 50%;
      }
      ${mediaQueries.md200} {
        width: 45%;
      }
      ${mediaQueries.lg} {
        width: 40%;
      }
      ${mediaQueries.xl} {
        width: 30%;
      }
      &:before {
        display: none;
      }
    }

    &__box {
      align-self: flex-end;
      padding-bottom: 0;
      width: 100%;
      max-width: none;
      ${mediaQueries.lg} {
        margin-bottom: 0;
      }
      a {
        &.hero-primary,
        &.hero-secondary {
          border-radius: 500px;
          text-decoration: none;
        }
      }
    }
    &__inner-wrapper {
      max-width: 100%;
      ${mediaQueries.tab} {
        max-width: 40%;
      }
      ${mediaQueries.md200} {
        width: 50%;
      }
      ${mediaQueries.lg} {
        width: 55%;
      }
      ${mediaQueries.xl} {
        width: 65%;
      }
      ${mediaQueries.lgLogo} {
        max-width: none;
      }
      span,
      h1 {
        color: ${colors.black};
        margin-bottom: 12px;
      }
      h1 {
        font-size: 48px;
        ${mediaQueries.lg} {
          font-size: 60px;
          line-height: 1.1;
        }
      }
    }
  }
`;

export const StyledTall200Hero = styled(StyledHero200Wrapper)<StyledHeroWrapperProps>`
  .hero {
    * {
      box-sizing: border-box;
    }
    .swiper-wrapper {
      align-items: flex-end;
    }
    &__image {
      position: relative;
      inset: 0;
      width: 100%;
      flex-shrink: 0;
      align-self: flex-end;
      z-index: 1;
      mask-image: none;
      ${mediaQueries.smHero200} {
        width: 80%;
        margin: 0 auto;
      }
      ${mediaQueries.tab} {
        width: 60%;
        position: absolute;
        z-index: 2;
        right: 0;
        left: 50%;
      }
      ${mediaQueries.md200} {
        width: 50%;
      }
      ${mediaQueries.lg} {
        width: 45%;
      }
      ${mediaQueries.xl} {
        width: 40%;
      }
      &:before {
        display: none;
      }

      img {
        position: relative;
        inset: 0;
      }
    }

    &__container {
      display: flex;
      position: relative;
      flex-direction: column;
      width: 100%;
      padding-top: 95px;
      justify-content: flex-end;
      max-height: 100svh;
      ${mediaQueries.tab} {
        flex-direction: row-reverse;
        align-items: stretch;
        min-height: 550px;
      }
      ${mediaQueries.lg} {
        padding-top: 165px;
        flex-direction: row-reverse;
        min-height: 700px;
      }
    }

    &__wrapper {
      height: auto;
      box-sizing: border-box;
      margin: -5% 0 0;
      padding: 2rem 1rem;
      flex-shrink: 0;
      gap: 0.5rem;
      z-index: 2;
      ${mediaQueries.sm} {
        margin-top: -15%;
      }
      ${mediaQueries.smHero} {
        margin-top: -22.5%;
      }
      ${mediaQueries.tab} {
        width: 100%;
        margin: 0;
        z-index: 1;
        padding-top: 80px;
        padding-bottom: 65px;
        align-self: flex-end;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        background-position: bottom right;
        padding-left: 2rem;
        padding-right: 2rem;
        max-width: none;
      }

      ${mediaQueries.lg} {
        padding-bottom: 100px;
      }
    }
    &__box {
      align-self: flex-end;
      margin-bottom: 1rem;
      margin-top: 0;
      width: 100%;
      ${mediaQueries.smHero200} {
        max-width: 60%;
      }
      ${mediaQueries.md} {
        max-width: 500px;
        margin-top: 2rem;
      }
      ${mediaQueries.lg} {
        align-self: flex-end;
        margin-bottom: 0;
      }
      a {
        &.hero-primary,
        &.hero-secondary {
          border-radius: 500px;
          text-decoration: none;
        }
      }
    }
    &__inner-wrapper {
      max-width: 100%;
      ${mediaQueries.lg} {
        max-width: none;
      }
      span,
      h1 {
        color: ${colors.black};
        margin-bottom: 12px;
      }
      h1 {
        font-size: 48px;
        word-wrap: normal;
        ${mediaQueries.lg} {
          font-size: 60px;
          line-height: 1.1;
        }
      }
    }
  }

  .swiper {
    width: 100%;
    height: auto;
  }
  .swiper-slide {
    display: flex;
    justify-content: center;
    height: auto;
    > * {
      user-select: none;
      touch-action: manipulation;

      ${mediaQueries.md} {
        user-select: auto;
        touch-action: auto;
      }
    }
  }

  .swiper-slide img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Slide controller */
  .slide-controller {
    &__wrapper {
      ${container};
      padding: 0 1rem!important;
      margin: 0 auto    !important;
      ${mediaQueries.tab} {
        padding: 0 2rem!important;
      }
    }
  }
  .swiper-pagination {
    display: none;
  }
`;

export const StyledSliderImg = styled.div`
  width: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  -webkit-perspective: 1000;
  -webkit-backface-visibility: hidden;
  -webkit-transform: translate3d(0, 0, 0);
`;

export const StyledSlideController = styled.div`
  position: absolute;
  text-align: center;
  transition: 0.3s opacity;
  transform: translate3d(0, 0, 0);
  bottom: 1rem;
  ${mediaQueries.lg} {
    bottom: ${(props) => (props.theme.key === 'zsl' ? '54px' : '84px')};
  }
  left: 0;
  width: 100%;
  z-index: 10;

  .slide-controller {
    &__wrapper {
      ${container}
      ${grid}
      grid-template-areas: 'page page page page page';
      ${mediaQueries.lg} {
        grid-template-areas: 'page page page page page page page page page page';
      }
    }
    &__container {
      grid-area: page;
      display: flex;
      width: 100%;
      align-items: center;
    }
    &__play {
      cursor: pointer;
      margin-right: 16px;
      width: ${(props) => (props.theme.key === 'zsl' ? '44px' : '48px')};
      height: ${(props) => (props.theme.key === 'zsl' ? '44px' : '48px')};
      background: url(${(props) =>
        props.theme.key === 'zsl' ? '/Hero/Play-zsl.svg' : (isBicentenary(props.theme.key) ? '/Hero/Play-bicentenary.svg' : '/Hero/Play-zoos.svg')});
      background-repeat: no-repeat;
      background-position: center;
      ${mediaQueries.lg} {
        display: block;
      }

      &--active {
        background: url(${(props) =>
          props.theme.key === 'zsl' ? '/Hero/Pause-zsl.svg' : (isBicentenary(props.theme.key) ? '/Hero/Pause-bicentenary.svg' : '/Hero/Pause-zoos.svg')});
        background-repeat: no-repeat;
      }
    }
    &__slide {
      cursor: pointer;
      height: 16px;
      width: 16px;
      border-radius: ${(props) => isBicentenary(props.theme.key) ? '5px' : props.theme.config.buttonRadius};
      opacity: 1;
      background-color: ${(props) => isBicentenary(props.theme.key) ? colors.black : (props.theme.key === 'zsl' ?  colors.seagrassDark : colors.whipsnadeBg)};
      border: ${(props) => (props.theme.key !== 'zsl' ? `1px solid transparent` : undefined)};
      margin-right: 8px;
      ${mediaQueries.lg} {
        margin-right: 12px;
        width: 24px;
        height: 24px;
      }
      &--active {
        background-color: ${(props) =>
          isBicentenary(props.theme.key) ? colors.black60 : (props.theme.key === 'zsl' ? colors.parakeet : props.theme.colors.button.primary.bg)};
        border: ${(props) => (props.theme.key !== 'zsl' ? `1px solid ${colors.white};` : undefined)};
      }
    }
  }
`;

export const StyledCampaignHero = styled(StyledHero)`
  .hero {
    &__wrapper {
      height: 580px;
      ${mediaQueries.lg} {
        height: 1029px;
        max-height: 90vh;
        min-height: 50rem;
      }
    }
    &__box {
      align-self: end;
      margin-bottom: 4rem;
      ${mediaQueries.lg} {
        align-self: center;
        margin-top: ${(props) => (props.theme.key === 'zsl' ? '8rem' : '3rem')};
        margin-bottom: 0;
      }
    }
    &__inner-wrapper {
      ${blackCircleGradient}
      max-width: 100%;
      ${mediaQueries.lg} {
        max-width: none;
      }
      span,
      h1 {
        text-shadow: ${(props) =>
    props.theme.key === 'zsl' ? '0px 0px 10px rgba(0, 0, 0, 1)' : undefined};
      }
      h1 {
        color: ${colors.white};
      }
      span {
        color: ${(props) => (props.theme.key !== 'zsl' ? colors.honeyBee : undefined)};
      }
    }
  }
`;

export const StyledDonateHero = styled(StyledHero)`
  .hero {
    &__wrapper {
      min-height: 408px;
      ${mediaQueries.lg} {
        height: 1029px;
        max-height: 90vh;
        min-height: 50rem;
      }
    }
    &__box {
      align-self: end;
      ${mediaQueries.lg} {
        align-self: center;
        margin-top: ${(props) => (props.theme.key === 'zsl' ? '8rem' : '3rem')};
        margin-bottom: 0;
      }
    }
    &__inner-wrapper {
      ${blackCircleGradient}
      &::before {
        left: 20%;
      }
      max-width: 100%;
      ${mediaQueries.lg} {
        max-width: none;
      }
      span,
      h1 {
        text-shadow: ${(props) =>
    props.theme.key === 'zsl' ? '0px 0px 10px rgba(0, 0, 0, 1)' : undefined};
      }
      h1 {
        color: ${colors.white};
      }
      span {
        color: ${(props) => (props.theme.key !== 'zsl' ? colors.honeyBee : undefined)};
      }
    }
  }
`;

export const StyledDonationHeroWrapper = styled.div`
  &.blur {
    filter: blur(5px) brightness(75%);
  }
`;

export const StyledDonationWrapperInner = styled.div`
  display: none;
  right: 2rem;
  width: 37.375rem;
  ${mediaQueries.lg} {
    position: absolute;
    display: flex;
    justify-content: flex-end;
    margin: 0 auto;
    bottom: 0;
    align-items: end;
  }
  ${mediaQueries.navxl} {
    right: 196px;
  }
`;

export const StyledDonationWrapper = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  z-index: 3;
  justify-content: center;
  margin: 0 auto;
  align-items: center;
  margin-bottom: 2rem;
  margin-top: -2rem;
  width: 100%;
  & > div {
    border-radius: 0;
  }
  ${mediaQueries.lg} {
    display: none;
  }
`;

export const StyledVoucherHero = styled(StyledHero)`
  .hero {
    &__wrapper {
      min-height: 408px;
      ${mediaQueries.lg} {
        height: 1029px;
        max-height: 90vh;
        min-height: 50rem;
      }
    }
    &__box {
      align-self: end;
      ${mediaQueries.lg} {
        align-self: center;
        margin-top: ${(props) => (props.theme.key === 'zsl' ? '8rem' : '3rem')};
        margin-bottom: 0;
      }
    }
    &__inner-wrapper {
      ${blackCircleGradient}
      &::before {
        left: 20%;
      }
      max-width: 100%;
      ${mediaQueries.lg} {
        max-width: none;
      }
      span,
      h1 {
        text-shadow: ${(props) =>
    props.theme.key === 'zsl' ? '0px 0px 10px rgba(0, 0, 0, 1)' : undefined};
      }
      h1 {
        color: ${colors.white};
      }
      span {
        color: ${(props) => (props.theme.key !== 'zsl' ? colors.honeyBee : undefined)};
      }
    }
  }
`;

export const StyledVoucherHeroWrapper = styled.div`
  &.blur {
    filter: blur(5px) brightness(75%);
  }
`;

export const StyledVoucherWrapperInner = styled.div`
  display: none;
  right: 100px;
  width: 37.375rem;
  ${mediaQueries.lg} {
    position: absolute;
    display: flex;
    justify-content: flex-end;
    margin: 0 auto;
    bottom: 0;
    align-items: end;
  }
  ${mediaQueries.navxl} {
    right: 196px;
  }
`;

export const StyledVoucherWrapper = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  z-index: 3;
  justify-content: center;
  margin: 0 auto;
  align-items: center;
  margin-bottom: 2rem;
  margin-top: -2rem;
  width: 100%;
  & > div {
    border-radius: 0;
  }
  ${mediaQueries.lg} {
    display: none;
  }
`;
